import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../components/ui/tooltip';
import {
  ArrowLeft, Gift, Users, Calendar, Loader2, Check, CheckCircle,
  SkipForward, Plus, Trash2, Search, RefreshCw, X, Eye, StopCircle,
  MessageSquare, Play, Settings
} from 'lucide-react';
import api from '../../services/api';

const WAAnniversaryFlowDetailPage = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('queue');

  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);

  // Entries state
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entrySearch, setEntrySearch] = useState('');
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [entriesTotalPages, setEntriesTotalPages] = useState(1);
  const [entriesTotalCount, setEntriesTotalCount] = useState(0);

  // Summary state
  const [summary, setSummary] = useState(null);

  // Template management
  const [templates, setTemplates] = useState([]);
  const [showAddYear, setShowAddYear] = useState(false);
  const [newYear, setNewYear] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');

  // Enrollment
  const [showEnroll, setShowEnroll] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [searching, setSearching] = useState(false);

  // Group enrollment
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Preview
  const [previewItem, setPreviewItem] = useState(null);

  // Skip reason
  const [skipReasonModal, setSkipReasonModal] = useState(null);
  const [skipReasonText, setSkipReasonText] = useState('');
  const SKIP_REASONS = ['WhatsApp disconnected', 'Customer not responding', 'Wrong number', 'Customer requested to stop', 'Duplicate contact', 'Other'];

  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [settingsAutoEnroll, setSettingsAutoEnroll] = useState(false);
  const [settingsOrderSources, setSettingsOrderSources] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [runningAutoEnroll, setRunningAutoEnroll] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState(false);
  const [availableOrderSources, setAvailableOrderSources] = useState([]);

  const openSettings = () => {
    setSettingsAutoEnroll(flow?.auto_enroll || false);
    setSettingsOrderSources(flow?.auto_enroll_order_sources || []);
    setShowSettings(true);
    // Fetch dynamic order sources
    api.get('/wa/anniversary-flows/order-sources').then(res => {
      setAvailableOrderSources(res.data || []);
    }).catch(() => {});
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put(`/wa/anniversary-flows/${flowId}`, {
        auto_enroll: settingsAutoEnroll,
        auto_enroll_order_sources: settingsOrderSources
      });
      fetchFlow();
      setShowSettings(false);
    } catch (e) { alert(e.response?.data?.detail || 'Error saving settings'); }
    setSavingSettings(false);
  };

  const handleRunAutoEnroll = async () => {
    setRunningAutoEnroll(true);
    try {
      const res = await api.post(`/wa/anniversary-flows/${flowId}/run-auto-enroll`);
      const { added, already_enrolled, skipped_masked } = res.data;
      alert(`Auto-enrolled ${added} new clients (${already_enrolled} already enrolled, ${skipped_masked || 0} masked phones skipped)`);
      fetchFlow();
      fetchEntries();
    } catch (e) { alert(e.response?.data?.detail || 'Error running auto-enroll'); }
    setRunningAutoEnroll(false);
  };

  const handleCleanup = async () => {
    if (!window.confirm('Remove duplicate entries and masked phone numbers from this flow?')) return;
    setRunningCleanup(true);
    try {
      const res = await api.post(`/wa/anniversary-flows/${flowId}/cleanup`);
      const { duplicates_removed, masked_removed, remaining } = res.data;
      alert(`Cleanup done: ${duplicates_removed} duplicates removed, ${masked_removed} masked entries removed. ${remaining} entries remaining.`);
      fetchFlow();
      fetchQueue();
      fetchEntries();
    } catch (e) { alert(e.response?.data?.detail || 'Error cleaning up'); }
    setRunningCleanup(false);
  };

  const fetchFlow = useCallback(async () => {
    try {
      const res = await api.get(`/wa/anniversary-flows/${flowId}`);
      setFlow(res.data);
    } catch { navigate('/whatsapp/fixed-flows'); }
    setLoading(false);
  }, [flowId, navigate]);

  const fetchQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await api.get(`/wa/anniversary-flows/${flowId}/queue`);
      setQueue(res.data);
    } catch {}
    setQueueLoading(false);
  };

  const fetchEntries = async (pg, pp, search) => {
    setEntriesLoading(true);
    try {
      const params = { page: pg || entriesPage, per_page: pp || entriesPerPage };
      if (search !== undefined ? search : entrySearch) params.search = search !== undefined ? search : entrySearch;
      const res = await api.get(`/wa/anniversary-flows/${flowId}/entries`, { params });
      setEntries(res.data.entries || []);
      setEntriesTotalPages(res.data.total_pages || 1);
      setEntriesTotalCount(res.data.total || 0);
    } catch {}
    setEntriesLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get(`/wa/anniversary-flows/${flowId}/summary`);
      setSummary(res.data);
    } catch {}
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/wa/templates');
      setTemplates(res.data);
    } catch {}
  };

  useEffect(() => { fetchFlow(); fetchTemplates(); }, [fetchFlow]);
  useEffect(() => {
    if (activeTab === 'queue') fetchQueue();
    else if (activeTab === 'clients') fetchEntries(entriesPage, entriesPerPage, entrySearch);
    else if (activeTab === 'summary') fetchSummary();
  }, [activeTab, flowId, entriesPage, entriesPerPage]);

  const handleMarkSent = async (entryId) => {
    try {
      await api.post(`/wa/anniversary-flows/${flowId}/entries/${entryId}/mark-sent`);
      fetchQueue();
      fetchFlow();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleStop = async (entryId) => {
    if (!window.confirm('Stop this client\'s anniversary flow?')) return;
    try {
      await api.delete(`/wa/anniversary-flows/${flowId}/entries/${entryId}`);
      fetchQueue();
      fetchFlow();
    } catch {}
  };

  const submitSkipReason = async () => {
    if (!skipReasonModal) return;
    try {
      await api.post(`/wa/anniversary-flows/${flowId}/entries/${skipReasonModal.entryId}/skip`, { reason: skipReasonText });
      fetchQueue();
    } catch {}
    setSkipReasonModal(null);
    setSkipReasonText('');
  };

  const handleAddYear = async () => {
    if (!newYear || !newTemplateId) return;
    const yearNum = parseInt(newYear);
    const existing = (flow?.year_templates || []).filter(yt => yt.year !== yearNum);
    const updated = [...existing, { year: yearNum, template_id: newTemplateId }].sort((a, b) => a.year - b.year);
    try {
      await api.put(`/wa/anniversary-flows/${flowId}`, { year_templates: updated });
      fetchFlow();
      setShowAddYear(false);
      setNewYear('');
      setNewTemplateId('');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleRemoveYear = async (year) => {
    if (!window.confirm(`Remove Year ${year} template?`)) return;
    const updated = (flow?.year_templates || []).filter(yt => yt.year !== year);
    try {
      await api.put(`/wa/anniversary-flows/${flowId}`, { year_templates: updated });
      fetchFlow();
    } catch {}
  };

  const handleSearchClients = async () => {
    if (!clientSearch.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/clients?search=${encodeURIComponent(clientSearch)}&limit=20`);
      const clients = res.data.clients || res.data;
      setSearchResults(clients.filter(c => c.first_order_date));
    } catch {}
    setSearching(false);
  };

  const handleEnroll = async () => {
    if (selectedClients.length === 0) return;
    setEnrolling(true);
    try {
      const res = await api.post(`/wa/anniversary-flows/${flowId}/entries`, { client_ids: selectedClients.map(c => c.id) });
      alert(res.data.message);
      setShowEnroll(false);
      setSelectedClients([]);
      setSearchResults([]);
      setClientSearch('');
      fetchFlow();
      fetchEntries();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setEnrolling(false);
  };

  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await api.get('/wa/groups');
      setGroups(res.data || []);
    } catch {}
    setLoadingGroups(false);
  };

  const handleAddGroup = async () => {
    if (!selectedGroup) return;
    setAddingGroup(true);
    try {
      const res = await api.post(`/wa/anniversary-flows/${flowId}/entries/from-group`, { group_id: selectedGroup });
      alert(res.data.message);
      setShowGroupModal(false);
      setSelectedGroup('');
      fetchFlow();
      fetchEntries();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add group'); }
    setAddingGroup(false);
  };

  const handleRemoveEntry = async (entryId) => {
    if (!window.confirm('Remove this client from the anniversary flow?')) return;
    try {
      await api.delete(`/wa/anniversary-flows/${flowId}/entries/${entryId}`);
      fetchEntries();
      fetchFlow();
    } catch {}
  };

  // Trigger search with debounce
  useEffect(() => {
    if (activeTab !== 'clients') return;
    const timer = setTimeout(() => {
      setEntriesPage(1);
      fetchEntries(1, entriesPerPage, entrySearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [entrySearch]);

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-[#4F46E5]" size={32} /></div>;
  if (!flow) return null;

  const templateMap = {};
  templates.forEach(t => { templateMap[t.id] = t; });

  const filteredEntries = entries; // Server-side filtered now

  return (
    <TooltipProvider>
    <div className="space-y-6">
      {/* Header — same style as Fixed Flow */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/whatsapp/fixed-flows')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {flow.name}
              <Badge className={flow.is_active ? "bg-green-100 text-green-700 ml-2" : "bg-gray-100 text-gray-500 ml-2"}>
                {flow.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchFlow(); fetchQueue(); if (activeTab === 'clients') fetchEntries(entriesPage, entriesPerPage, entrySearch); }}><RefreshCw size={16} className="mr-2" /> Refresh</Button>
          <Button variant="outline" onClick={openSettings} data-testid="settings-btn"><Settings size={16} className="mr-2" /> Settings</Button>
          <Button variant="outline" onClick={handleRunAutoEnroll} disabled={runningAutoEnroll} data-testid="run-auto-enroll-btn">
            {runningAutoEnroll ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
            {runningAutoEnroll ? 'Enrolling...' : 'Run Auto-Enroll'}
          </Button>
          <Button variant="outline" onClick={handleCleanup} disabled={runningCleanup} className="text-orange-600 border-orange-300 hover:bg-orange-50" data-testid="cleanup-btn">
            {runningCleanup ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Trash2 size={16} className="mr-2" />}
            {runningCleanup ? 'Cleaning...' : 'Cleanup'}
          </Button>
          <Button variant="outline" onClick={() => { setShowGroupModal(true); fetchGroups(); }}>
            <Users size={16} className="mr-2" /> Add Client Group
          </Button>
          <Button onClick={() => setShowEnroll(true)} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="enroll-clients-btn">
            <Plus size={16} className="mr-2" /> Add Clients
          </Button>
        </div>
      </div>

      {/* Stats Cards — matching Fixed Flow layout */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <Users className="text-gray-500" size={20} />
            <div>
              <p className="text-2xl font-bold text-gray-900">{flow.stats?.total_clients || 0}</p>
              <p className="text-sm text-gray-500">Total Enrolled</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <Play className="text-green-500" size={20} />
            <div>
              <p className="text-2xl font-bold text-green-600">{flow.stats?.active || 0}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <MessageSquare className="text-purple-500" size={20} />
            <div>
              <p className="text-2xl font-bold text-purple-600">{flow.stats?.sent || 0}</p>
              <p className="text-sm text-gray-500">Clients Sent</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <Gift className="text-[#4F46E5]" size={20} />
            <div>
              <p className="text-2xl font-bold text-[#4F46E5]">{flow.year_templates?.length || 0}</p>
              <p className="text-sm text-gray-500">Year Templates</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Year Templates */}
      <Card className="bg-white border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Year Templates</h2>
          <Button variant="outline" size="sm" onClick={() => setShowAddYear(true)} data-testid="add-year-btn">
            <Plus size={14} className="mr-1" /> Add Year
          </Button>
        </div>
        <div className="p-4">
          {(!flow.year_templates || flow.year_templates.length === 0) ? (
            <p className="text-gray-400 text-sm text-center py-4">No year templates yet. Add one to get started.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {flow.year_templates.map(yt => {
                const tpl = templateMap[yt.template_id];
                return (
                  <div key={yt.year} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                    <span className="text-sm font-bold text-[#4F46E5]">Year {yt.year}</span>
                    <span className="text-xs text-gray-500">→ {tpl?.name || 'Unknown template'}</span>
                    <button onClick={() => handleRemoveYear(yt.year)} className="text-gray-400 hover:text-red-500 ml-1"><X size={12} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Tabs — matching Fixed Flow */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button onClick={() => setActiveTab('queue')} className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${activeTab === 'queue' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Today's Queue ({queue.length})
          </button>
          <button onClick={() => setActiveTab('clients')} className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${activeTab === 'clients' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            All Clients ({entriesTotalCount})
          </button>
          <button onClick={() => setActiveTab('summary')} className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${activeTab === 'summary' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Summary
          </button>
        </nav>
      </div>

      {/* Today's Queue — matching Fixed Flow layout exactly */}
      {activeTab === 'queue' && (
        <Card className="bg-white border border-gray-100 overflow-hidden">
          {queueLoading ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-[#4F46E5]" size={32} /></div>
          ) : queue.length === 0 ? (
            <div className="p-12 text-center">
              <Gift className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Anniversaries Due Today</h3>
              <p className="text-gray-500">Anniversary messages will appear here when a client's purchase date matches today.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Anniversary</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {queue.map((item, idx) => (
                    <tr key={item.entry_id} className={`hover:bg-gray-50 ${item.tracking_skip_reason ? 'bg-amber-50/50' : ''}`}>
                      <td className="px-4 py-4 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-900">{item.client_name}</p>
                        {item.tracking_skip_reason && (
                          <p className="text-xs text-amber-600 mt-0.5">{item.tracking_skip_reason}</p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-gray-600">{item.client_phone}</td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                          Year {item.anniversary_year}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          ⏱ Pending
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Button variant="ghost" size="sm" onClick={() => setPreviewItem(item)} className="text-gray-500">
                          <Eye size={14} className="mr-1" /> Preview
                        </Button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a href={item.wa_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#25D366] text-white text-sm rounded-lg hover:bg-[#20bd5a] transition-colors">
                                <MessageSquare size={14} /> Open WhatsApp
                              </a>
                            </TooltipTrigger>
                            <TooltipContent><p>Open WhatsApp with pre-filled message</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => handleMarkSent(item.entry_id)} className="text-green-600 border-green-200 hover:bg-green-50">
                                <CheckCircle size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Mark as Sent</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => { setSkipReasonModal({ entryId: item.entry_id }); setSkipReasonText(''); }} className="text-yellow-600 border-yellow-200 hover:bg-yellow-50">
                                <SkipForward size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Skip this message</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => handleStop(item.entry_id)} className="text-red-600 border-red-200 hover:bg-red-50">
                                <StopCircle size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Stop this client's flow</p></TooltipContent>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* All Clients Tab */}
      {activeTab === 'clients' && (
        <Card className="bg-white border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-gray-900">Enrolled Clients</h3>
              <span className="text-sm text-gray-500">({entriesTotalCount} total)</span>
            </div>
            <div className="flex items-center gap-3">
              <select 
                value={entriesPerPage} 
                onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setEntriesPage(1); }}
                className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-600 bg-white"
                data-testid="entries-per-page"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
              <Input placeholder="Search..." value={entrySearch} onChange={(e) => setEntrySearch(e.target.value)} className="w-48" />
            </div>
          </div>
          {entriesLoading ? (
            <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-[#4F46E5]" size={24} /></div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No clients found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">First Order</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Next Anniversary</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEntries.map(entry => {
                    const fod = entry.first_order_date ? new Date(entry.first_order_date) : null;
                    const now = new Date();
                    let nextAnniversary = null;
                    let nextYear = null;
                    if (fod) {
                      const thisYearAnniv = new Date(now.getFullYear(), fod.getMonth(), fod.getDate());
                      if (thisYearAnniv > now) {
                        nextAnniversary = thisYearAnniv;
                        nextYear = now.getFullYear() - fod.getFullYear();
                      } else {
                        nextAnniversary = new Date(now.getFullYear() + 1, fod.getMonth(), fod.getDate());
                        nextYear = now.getFullYear() + 1 - fod.getFullYear();
                      }
                    }
                    const sentYears = entry.sent_years || [];
                    const skippedYears = entry.skipped_years || [];
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{entry.client_name}</p>
                          <p className="text-xs text-gray-400">{entry.client_phone}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {fod ? fod.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {nextAnniversary && entry.status === 'active' ? (
                            <div>
                              <p className="text-sm font-medium text-gray-800">{nextAnniversary.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                              <p className="text-xs text-purple-600">Year {nextYear}</p>
                            </div>
                          ) : <span className="text-gray-400 text-sm">-</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {sentYears.map(y => <Badge key={y} className="bg-green-100 text-green-700 text-xs">Y{y} ✓</Badge>)}
                            {skippedYears.map((sy, i) => (
                              <Tooltip key={i}><TooltipTrigger><Badge className="bg-amber-100 text-amber-700 text-xs cursor-help">Y{sy.year} ⏭</Badge></TooltipTrigger>
                              <TooltipContent><p>{sy.reason || 'Skipped'}</p></TooltipContent></Tooltip>
                            ))}
                            {sentYears.length === 0 && skippedYears.length === 0 && <span className="text-xs text-gray-400">None yet</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={entry.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>{entry.status}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveEntry(entry.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination Controls */}
          {entriesTotalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {entriesPage} of {entriesTotalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEntriesPage(p => Math.max(1, p - 1))} disabled={entriesPage === 1}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEntriesPage(p => Math.min(entriesTotalPages, p + 1))} disabled={entriesPage === entriesTotalPages}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Clients', value: summary.total_clients, color: 'text-gray-900' },
              { label: 'Active', value: summary.active, color: 'text-green-600' },
              { label: 'Messages Sent', value: summary.total_sent, color: 'text-purple-600' },
              { label: 'Messages Skipped', value: summary.total_skipped, color: 'text-amber-600' }
            ].map(s => (
              <Card key={s.label} className="bg-white border border-gray-100 p-4">
                <p className="text-sm text-gray-500">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </Card>
            ))}
          </div>
          {Object.keys(summary.year_stats || {}).length > 0 && (
            <Card className="bg-white border border-gray-100 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Per-Year Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(summary.year_stats).sort(([a], [b]) => a - b).map(([year, stats]) => (
                  <div key={year} className="flex items-center gap-4">
                    <span className="text-sm font-bold text-[#4F46E5] w-16">Year {year}</span>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden flex">
                        <div className="bg-green-500 h-3" style={{ width: `${(stats.sent / (stats.sent + stats.skipped || 1)) * 100}%` }} />
                        <div className="bg-amber-400 h-3" style={{ width: `${(stats.skipped / (stats.sent + stats.skipped || 1)) * 100}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-24">{stats.sent} sent, {stats.skipped} skipped</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {summary.upcoming_this_week?.length > 0 && (
            <Card className="bg-white border border-gray-100 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Upcoming This Week</h3>
              <div className="space-y-2">
                {summary.upcoming_this_week.map((u, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <Badge className="bg-purple-100 text-purple-700">Y{u.anniversary_year}</Badge>
                    <span className="font-medium text-gray-800">{u.client_name}</span>
                    <span className="text-gray-400">{u.client_phone}</span>
                    <span className="text-gray-500 ml-auto">{u.days_away === 0 ? 'Today' : u.days_away === 1 ? 'Tomorrow' : `In ${u.days_away} days`}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={(open) => { if (!open) setPreviewItem(null); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>Message Preview</DialogTitle></DialogHeader>
          {previewItem && (
            <div className="space-y-3">
              <div className="text-sm text-gray-500">{previewItem.client_name} • Year {previewItem.anniversary_year}</div>
              <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap">{previewItem.message_content}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Year Template Dialog */}
      <Dialog open={showAddYear} onOpenChange={setShowAddYear}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Add Year Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-gray-700">Year Number</label>
              <Input type="number" min="1" placeholder="e.g. 1, 2, 3" value={newYear} onChange={(e) => setNewYear(e.target.value)} /></div>
            <div><label className="text-sm font-medium text-gray-700">Template</label>
              <select value={newTemplateId} onChange={(e) => setNewTemplateId(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-1">
                <option value="">Select template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddYear(false)}>Cancel</Button>
              <Button onClick={handleAddYear} disabled={!newYear || !newTemplateId} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enroll Clients Dialog */}
      <Dialog open={showEnroll} onOpenChange={setShowEnroll}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>Enroll Clients</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-400">Only clients with a first order date can be enrolled.</p>
            <div className="flex gap-2">
              <Input placeholder="Search by name or phone..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchClients()} />
              <Button onClick={handleSearchClients} disabled={searching} variant="outline">
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                {searchResults.map(c => {
                  const isSelected = selectedClients.some(sc => sc.id === c.id);
                  return (
                    <div key={c.id} className={`px-3 py-2 flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                      onClick={() => { if (isSelected) setSelectedClients(prev => prev.filter(sc => sc.id !== c.id)); else setSelectedClients(prev => [...prev, c]); }}>
                      <div>
                        <p className="font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-400">{c.phone} • First order: {c.first_order_date ? new Date(c.first_order_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</p>
                      </div>
                      {isSelected && <Check size={16} className="text-[#4F46E5]" />}
                    </div>
                  );
                })}
              </div>
            )}
            {selectedClients.length > 0 && <p className="text-sm text-[#4F46E5] font-medium">{selectedClients.length} client(s) selected</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowEnroll(false)}>Cancel</Button>
              <Button onClick={handleEnroll} disabled={enrolling || selectedClients.length === 0} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
                {enrolling ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Enroll {selectedClients.length} Client(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skip Reason Dialog — matching Fixed Flow design */}
      <Dialog open={!!skipReasonModal} onOpenChange={(open) => { if (!open) { setSkipReasonModal(null); setSkipReasonText(''); } }}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Skip Message — Reason</h3>
            <div className="space-y-2">
              {SKIP_REASONS.map((r) => (
                <button key={r} onClick={() => setSkipReasonText(r)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${skipReasonText === r ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                  {r}
                </button>
              ))}
            </div>
            <Input placeholder="Or type a custom reason..." value={SKIP_REASONS.includes(skipReasonText) ? '' : skipReasonText} onChange={(e) => setSkipReasonText(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setSkipReasonModal(null); setSkipReasonText(''); }}>Cancel</Button>
              <Button size="sm" onClick={submitSkipReason} className="bg-amber-500 hover:bg-amber-600 text-white">Skip</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client Group Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Client Group to Anniversary Flow</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Select a group. All members with a first order date will be enrolled.</p>
            {loadingGroups ? (
              <div className="text-center py-4"><Loader2 className="animate-spin mx-auto text-[#4F46E5]" size={24} /></div>
            ) : (
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="select-group"
              >
                <option value="">Select group...</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name} ({g.member_count || 0} members)</option>
                ))}
              </select>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowGroupModal(false)}>Cancel</Button>
              <Button onClick={handleAddGroup} disabled={addingGroup || !selectedGroup} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="add-group-confirm">
                {addingGroup ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Add Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Anniversary Flow Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Auto-enroll */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsAutoEnroll}
                  onChange={(e) => setSettingsAutoEnroll(e.target.checked)}
                  className="rounded"
                  data-testid="auto-enroll-checkbox"
                />
                <span className="text-sm text-gray-700">Auto-enroll clients</span>
              </label>
              <p className="text-xs text-gray-400 ml-6">Automatically enroll clients with a first order date into this anniversary flow.</p>

              {/* Order Source Filters — shown when auto-enroll is ON */}
              {settingsAutoEnroll && (
                <div className="ml-6 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Enrollment Filters</p>
                  <div>
                    <p className="text-sm text-gray-700 mb-1.5">Order Sources (tick to include, empty = all):</p>
                    <div className="flex flex-wrap gap-3">
                      {availableOrderSources.map((source) => (
                        <label key={source} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settingsOrderSources.includes(source)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSettingsOrderSources(prev => [...prev, source]);
                              } else {
                                setSettingsOrderSources(prev => prev.filter(s => s !== source));
                              }
                            }}
                            className="rounded"
                            data-testid={`filter-source-${source}`}
                          />
                          <span className="text-sm text-gray-700">{source}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="save-settings-btn">
                {savingSettings && <Loader2 className="animate-spin mr-2" size={16} />}
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default WAAnniversaryFlowDetailPage;
